import { upperFirst } from 'lodash-es'

import { ContentfulType } from '~/types/references'
import { RelatedPost } from '../ContentfulEntries/RelatedPost'
import { ExpertTip } from '../ContentfulEntries/ExpertTip'
import { Video } from '../ContentfulEntries/Video'
import { CopyableBox } from '../ContentfulEntries/CopyableBox'
import { StatisticalInsight } from '../ContentfulEntries/StatisticalInsight'
import { DoDontTable } from '../ContentfulEntries/DoDontTable'
import { CallOutBox } from '../ContentfulEntries/CallOutBox'
import { ResourcesBox } from '../ContentfulEntries/ResourcesBox'

import type { CustomElementMap } from '~/types/references'
import { BlogImage } from '../ContentfulEntries/BlogImage'
import { EntryInline } from '../ContentfulEntries/EntryInline'
import type { Entry, EntrySkeletonType } from 'contentful'
import { deconstructNode } from '~/libs/contentful/helpers'
import { InteractiveBanner } from '~/components/ContentfulEntries/InteractiveBanner'
import { HighlightedText } from '~/components/ContentfulEntries/HighlightedText'
import { ExpertTipV2 } from '~/components/ContentfulEntries/ExpertTipV2'
import { DescriptiveSlider } from '~/components/ContentfulEntries/DescriptiveSlider'
import { TopSkills } from '~/components/ContentfulEntries/TopSkills'
import { SalaryRange } from '~/components/ContentfulEntries/SalaryRange'
import { SalaryByLocation } from '~/components/ContentfulEntries/SalaryByLocation'
import { SalaryByExperience } from '~/components/ContentfulEntries/SalaryByExperience'
import { EntryLinkBlock, Node } from '@contentful/rich-text-types'
import {
  TypeArticle,
  TypeBlogImage,
  TypeCallOut,
  TypeCopyableBox,
  TypeDescriptiveSlider,
  TypeDoDont,
  TypeExpertTip,
  TypeExpertTipV2,
  TypeHighlightedText,
  TypeInteractiveBanner,
  TypeResources,
  TypeSalaryByExperience,
  TypeSalaryByLocation,
  TypeStatisticalInsights,
  TypeTopSkills,
  TypeVideoUpload,
} from '~/types/contentful'

type EmbeddedEntryProps = {
  node: EntryLinkBlock
  children?: React.ReactNode
}

export enum RichTextBlockType {
  blogImage = 'blogImage',
  callOut = 'callOut',
  copyableBox = 'copyableBox',
  doDont = 'doDont',
  expertTip = 'expertTip',
  expertTipV2 = 'expertTipV2',
  resources = 'resources',
  statisticalInsights = 'statisticalInsights',
  interactiveBanner = 'interactiveBanner',
  highlightedText = 'highlightedText',
  descriptiveSlider = 'descriptiveSlider',
  topSkills = 'topSkills',
  salaryRange = 'salaryRange',
  salaryByLocation = 'salaryByLocation',
  salaryByExperience = 'salaryByExperience',
}

/**
 *
 * EmbeddedEntry is a generic component that renders a Contentful embedded entry in rich text.
 */

export const EmbeddedEntry = ({ node, children }: EmbeddedEntryProps) => {
  const { nodeType } = node as Node
  const { sys } = deconstructNode(node)
  if (!sys) return <></>
  const { id, contentType } = sys

  const contentTypeName = upperFirst(contentType?.sys?.type) as ContentfulType
  const contentTypeId =
    contentType?.sys?.id ||
    (node as unknown as Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string>).sys
      ?.contentType?.sys?.id

  let CustomElement = elements[contentTypeName]

  if (nodeType == 'embedded-entry-inline') {
    CustomElement = elements[ContentfulType.InlineAsset]
  }

  if (contentTypeId == 'videoUpload') {
    CustomElement = elements['Video']
  }
  if (contentTypeId === 'article') {
    CustomElement = elements[ContentfulType.Article]
  }

  if (contentTypeId == 'copyableBox') {
    CustomElement = elements['CopyableBox' as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.callOut) {
    CustomElement = elements[ContentfulType.CallOut as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.resources) {
    CustomElement = elements[ContentfulType.Resources as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.copyableBox) {
    CustomElement = elements[ContentfulType.CopyableBox as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.expertTip) {
    CustomElement = elements[ContentfulType.ExpertTip as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.expertTipV2) {
    CustomElement = elements[ContentfulType.ExpertTipV2 as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.interactiveBanner) {
    CustomElement = elements[ContentfulType.InteractiveBanner as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.highlightedText) {
    CustomElement = elements[ContentfulType.HighlightedText as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.statisticalInsights) {
    CustomElement = elements[ContentfulType.StatisticalInsight as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.doDont) {
    CustomElement = elements[ContentfulType.DoDont as ContentfulType]
  }
  if (contentTypeId == RichTextBlockType.blogImage) {
    CustomElement = elements[ContentfulType.BlogImage as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.descriptiveSlider) {
    CustomElement = elements[ContentfulType.DescriptiveSlider as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.topSkills) {
    CustomElement = elements[ContentfulType.TopSkills as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.salaryRange) {
    CustomElement = elements[ContentfulType.SalaryRange as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.salaryByLocation) {
    CustomElement = elements[ContentfulType.SalaryByLocation as ContentfulType]
  }

  if (contentTypeId == RichTextBlockType.salaryByExperience) {
    CustomElement = elements[ContentfulType.SalaryByExperience as ContentfulType]
  }

  if (!id || !CustomElement) {
    return <DebugComponent node={node}>{children}</DebugComponent>
  }

  return (
    <CustomElement
      node={node}
      reference={
        node.data.target as unknown as Entry<
          EntrySkeletonType,
          'WITHOUT_UNRESOLVABLE_LINKS',
          string
        >
      }
    >
      {children}
    </CustomElement>
  )
}

export const elements: Partial<CustomElementMap> = {
  // [ContentfulType.Asset]: ({ reference }) => (
  //   <div className="flex flex-col w-full mlb-12 max-xl:mlb-10 max-md:mlb-7">
  //     <ResponsiveImage asset={reference.fields as unknown as AssetFields} />
  //   </div>
  // ),
  [ContentfulType.ExpertTip]: ({ reference }) => (
    <ExpertTip expertTip={reference as TypeExpertTip<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.ExpertTipV2]: ({ reference }) => (
    <ExpertTipV2 expertTipV2={reference as TypeExpertTipV2<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.DescriptiveSlider]: ({ reference }) => (
    <DescriptiveSlider
      descriptiveSlider={reference as TypeDescriptiveSlider<'WITHOUT_UNRESOLVABLE_LINKS', string>}
    />
  ),
  [ContentfulType.InteractiveBanner]: ({ reference }) => (
    <InteractiveBanner
      banner={reference as TypeInteractiveBanner<'WITHOUT_UNRESOLVABLE_LINKS', string>}
    />
  ),
  [ContentfulType.HighlightedText]: ({ reference }) => (
    <HighlightedText
      data={reference as TypeHighlightedText<'WITHOUT_UNRESOLVABLE_LINKS', string>}
    />
  ),
  [ContentfulType.CopyableBox]: ({ reference }) => (
    <CopyableBox copyableBox={reference as TypeCopyableBox<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.StatisticalInsight]: ({ reference }) => (
    <StatisticalInsight
      statisticalInsight={
        reference as TypeStatisticalInsights<'WITHOUT_UNRESOLVABLE_LINKS', string>
      }
    />
  ),
  [ContentfulType.DoDont]: ({ reference }) => (
    <DoDontTable doDont={reference as TypeDoDont<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.Article]: ({ reference }) => (
    <RelatedPost post={reference as TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.Video]: ({ reference }) => (
    <Video
      asset={reference as TypeVideoUpload<'WITHOUT_UNRESOLVABLE_LINKS', string>}
      height={'auto'}
    />
  ),
  [ContentfulType.CallOut]: ({ reference }) => (
    <CallOutBox callOut={reference as TypeCallOut<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.Resources]: ({ reference }) => (
    <ResourcesBox callOut={reference as TypeResources<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.BlogImage]: ({ reference }) => (
    <BlogImage blogAsset={reference as TypeBlogImage<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.InlineAsset]: ({ reference }) => <EntryInline asset={reference} />,
  [ContentfulType.TopSkills]: ({ reference }) => (
    <TopSkills topSkills={reference as TypeTopSkills<'WITHOUT_UNRESOLVABLE_LINKS', string>} />
  ),
  [ContentfulType.SalaryRange]: () => <SalaryRange />,
  [ContentfulType.SalaryByLocation]: ({ reference }) => (
    <SalaryByLocation
      salaryByLocation={reference as TypeSalaryByLocation<'WITHOUT_UNRESOLVABLE_LINKS', string>}
    />
  ),
  [ContentfulType.SalaryByExperience]: ({ reference }) => (
    <SalaryByExperience
      salaryByExperience={reference as TypeSalaryByExperience<'WITHOUT_UNRESOLVABLE_LINKS', string>}
    />
  ),
}

const DebugComponent = (props: EmbeddedEntryProps) => {
  if (process.env.NODE_ENV === 'production') {
    return null
  }

  return (
    <pre>
      <code>{JSON.stringify(props, null, 2)}</code>
    </pre>
  )
}
